/**
 * Worker API client. Set VITE_API_BASE in env or it defaults to the
 * production parkify-pay-api Worker.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)
  ?? 'https://api.parkify.com'

export interface NoticeView {
  id: string
  zone_id: string
  property_name: string
  property_address: string | null
  plate: string
  plate_state: string | null
  violation_label: string
  observed_at: string
  issued_at: string
  status: 'unpaid' | 'paid' | 'disputed' | 'voided' | 'escalated'
  base_amount_cents: number
  admin_fee_cents: number
  tax_cents: number
  total_cents: number
  escalation_at: string
  escalated_amount_cents: number
}

export interface PriceBreakdown {
  base_cents: number
  admin_fee_cents: number
  tax_cents: number
  total_cents: number
  promo_code: string | null
  promo_discount_cents: number
  escalated: boolean
}

async function jget<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`)
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? r.statusText)
  return r.json() as Promise<T>
}
async function jpost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? r.statusText)
  return r.json() as Promise<T>
}

// The Worker reports which Stripe mode its sk_* is in. Compared against the
// client's pk_* mode by StripeModeGuard to catch a silent test/live mismatch.
export async function fetchStripeMode(
  signal?: AbortSignal,
): Promise<'test' | 'live' | 'unknown'> {
  const r = await fetch(`${API_BASE}/api/payments/stripe-mode`, { signal })
  if (!r.ok) throw new Error(r.statusText)
  const data = (await r.json().catch(() => null)) as { mode?: string } | null
  return data?.mode === 'test' || data?.mode === 'live' ? data.mode : 'unknown'
}

export const api = {
  lookup: (q: { plate?: string; notice?: string }) => {
    const p = new URLSearchParams()
    if (q.plate) p.set('plate', q.plate)
    if (q.notice) p.set('notice', q.notice)
    return jget<{ notices: NoticeView[] }>(`/api/enforcement/lookup?${p}`)
  },
  detail: (id: string) =>
    jget<{ notice: NoticeView; price: PriceBreakdown }>(`/api/enforcement/notice/${encodeURIComponent(id)}`),
  applyPromo: (id: string, code: string) =>
    jpost<{ valid: boolean; price: PriceBreakdown }>(`/api/enforcement/notice/${encodeURIComponent(id)}/promo`, { code }),
  upsertParker: (id: string, body: { email?: string; phone?: string; firstName?: string; lastName?: string; optIn?: boolean }) =>
    jpost<{ parker_id: string; opted_in: boolean }>(`/api/enforcement/notice/${encodeURIComponent(id)}/parker`, body),
  createIntent: (id: string, body: { promo?: string }) =>
    jpost<{ client_secret: string; amount: number; txn_id: string; price: PriceBreakdown }>(
      `/api/enforcement/notice/${encodeURIComponent(id)}/intent`, body,
    ),
}
